import { useEffect, useState } from "react";
import {
  Avatar,
  Input,
  Table,
  TrashIcon,
  SearchIcon,
  NotifierNotification,
  ConfirmationModal,
} from "@mwi/ui";
import useTitle from "hooks/useTitle";
import { useDeleteUserMutation, useGetUsersQuery } from "api";
import { DateTime } from "luxon";
import { useDebounce } from "use-debounce";
import { useNotifier } from "react-headless-notifier";
import User from "api/types/models/user";
import UserRole from "api/types/enums/UserRole";

type ItemProps = {
  user: User;
  onClick: (action?: "delete") => void;
};

const Item = ({ user, onClick }: ItemProps): JSX.Element => {
  return (
    <Table.Row key={user.id}>
      <Table.Item className="flex items-center gap-2">
        <Avatar
          className="w-10 h-10"
          name={
            user.first_name ? `${user.first_name} ${user.last_name}` : undefined
          }
        />
        {user.first_name ? `${user.first_name} ${user.last_name}` : "New User"}
      </Table.Item>
      <Table.Item>{user.email}</Table.Item>
      <Table.Item>
        {user.last_active_at ? (
          DateTime.fromISO(user.last_active_at).toFormat("dd.MM.yyyy")
        ) : (
          <div className="space-x-2">
            <span className="font-semibold text-light">Pending</span>
          </div>
        )}
      </Table.Item>
      <Table.Item>
        <div
          className="inline-block cursor-pointer"
          onClick={() => onClick("delete")}
        >
          <TrashIcon className="w-6 h-6 text-error" />
        </div>
      </Table.Item>
    </Table.Row>
  );
};

const OwnerManagementPage = (): JSX.Element => {
  useTitle("Manage Owners");

  const { notify } = useNotifier();

  const [search, setSearch] = useState<string>();
  const [deletingUserId, setDeletingUserId] = useState<number | undefined>();

  const [debouncedSearch] = useDebounce(search, 400);

  const { data: users, isFetching } = useGetUsersQuery({
    search: debouncedSearch,
    role: UserRole.OWNER,
  });

  const [deleteUser, { isLoading: isDeleting, isSuccess: didDeleteUser }] =
    useDeleteUserMutation();

  const handleUser = async (user: User, action?: "delete") => {
    if (action === "delete") {
      setDeletingUserId(user.id);
    }
  };

  useEffect(() => {
    if (didDeleteUser) {
      setDeletingUserId(undefined);
      notify(
        <NotifierNotification
          title="User Deleted"
          message="User was successfully deleted"
        />,
      );
    }
  }, [didDeleteUser]);

  return (
    <div className="grid tablet:grid-cols-8 desktop:grid-cols-10 !pb-16 gap-x-6">
      <div className="flex flex-col gap-y-[2px] col-span-full mb-14 text-lg">
        <p className="font-semibold">
          Manage the owners who belong to this practice
        </p>
        <div>
          <p>Here you have the option to delete owner accounts</p>
        </div>
      </div>

      <div className="mb-8 col-span-full tablet:col-span-2 desktop:col-span-3">
        <Input
          placeHolder="Search users"
          prefixIcon={<SearchIcon />}
          size="small"
          onChange={(e) =>
            setSearch(e.target.value ? e.target.value : undefined)
          }
        />
      </div>

      <div className="col-span-full">
        <Table loading={isFetching}>
          <Table.Head>
            <Table.Row>
              <Table.Item as="th">Name</Table.Item>
              <Table.Item as="th">Email</Table.Item>
              <Table.Item as="th">Last Active</Table.Item>
              <Table.Item as="th"></Table.Item>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {users && users.length ? (
              users.map((user) => (
                <Item
                  key={user.id}
                  user={user}
                  onClick={(action) => handleUser(user, action)}
                />
              ))
            ) : (
              <Table.Row>
                <Table.Item colSpan={5} className="text-center">
                  No users found
                </Table.Item>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>

      <ConfirmationModal
        isOpen={deletingUserId !== undefined}
        onClose={() => setDeletingUserId(undefined)}
        title="Delete User"
        description="Are you sure you want to delete this user?"
        confirmText="Delete"
        confirmLoading={isDeleting}
        onConfirm={() => deleteUser(deletingUserId!)}
        danger
      />
    </div>
  );
};

export default OwnerManagementPage;
